import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  NavLink,
  useLocation,
  useParams,
  generatePath,
  Outlet,
} from 'react-router-dom';
import {
  Table,
  useQueryState,
  useFilters,
  useScroll,
  usePrevious,
  useGetList,
  IconExpand,
  formatDate,
  SchemaFilterType,
  PermissionEnum,
  PermissionDenied,
  SelectItemProps,
  IconLoader,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import {
  useLazyGetPSPExternalAccountsListQuery,
  useLazyGetPspPartnerQuery,
  useLazyGetBankCASPQuery,
} from './store/services';
import {
  columnsItems,
  columnsLabels,
  filtersSchema,
  schema,
} from './variables/external';
import { ExternalAccount } from './types';
import {
  setTakeCount as setStoreTakeCount,
  clearExternalAccountsState,
} from './store/slices/info';
import { PaymentType } from '../../store/dictionaries/types';
import Card from '../../components/partial/card/card';
import useGetAccountsAutocompleteList from './helpers/use-get-accounts';

import { BankRoutes } from '../../routes';

import cn from './bank-and-psp.module.css';

const formatExternalList = (
  list?: ExternalAccount[],
  paymentTypes?: PaymentType[],
  casp?: SelectItemProps[],
  currenciesList?: SelectItemProps[],
): ExternalAccount[] => {
  const nextList = list?.map((item) => {
    const result = { ...item };

    const availablePaymentTypes: string[] = [];

    item?.pspIntegrationPayment?.paymentsTypes?.forEach((type) => {
      if (
        paymentTypes?.find((paymentType) => paymentType.paymentType === type)
      ) {
        availablePaymentTypes.push(
          paymentTypes?.find((paymentType) => paymentType.paymentType === type)
            ?.name as string,
        );
      }
    });

    const currentCurrencies: string[] = [];

    if (item?.currenciesIds?.length) {
      item?.currenciesIds?.forEach((currencyId) => {
        currentCurrencies.push(
          currenciesList?.find((currency) => currency.value === currencyId)
            ?.name as string,
        );
      });
    }

    const render: Record<string, any> = {
      accountId: (
        <div>
          {item?.account?.fullNumber}
          <div className={cn.externalAccountCustomerName}>
            {item?.account?.description}
          </div>
        </div>
      ),
      caspId: (
        <div>
          {item?.caspId ? (
            casp?.find((caspItem) => caspItem.value === result.caspId)?.name
          ) : (
            <div className={cn.emptyTableValue} />
          )}
        </div>
      ),
      createdAt: <div>{formatDate(item?.createdAt as string)}</div>,
      currencyId: (
        <div className={cn.currenciesWrapper}>
          {currentCurrencies?.join(', ') || '-'}
        </div>
      ),
      paymentType: (
        <div className={cn.paymentTypesRow}>
          {availablePaymentTypes?.join(', ') || '-'}
        </div>
      ),
      accountType: (
        <div>
          {item?.account?.organization?.type
            ? `${item?.account?.organization?.type.charAt(
                0,
              )}${item?.account?.organization?.type.slice(1).toLowerCase()}`
            : '-'}
        </div>
      ),
      active: (
        <div className={!item.active ? cn.disabledStatus : ''}>
          {item?.active ? 'Enabled' : 'Disabled'}
        </div>
      ),
      createType: (
        <div>
          {result.createType
            ? `${result.createType.charAt(0)}${result.createType
                .slice(1)
                .toLowerCase()}`
            : ''}
        </div>
      ),
    };

    result.render = render;

    return result;
  });

  return nextList as ExternalAccount[];
};

const PSPExternalAccounts = () => {
  const [takeCount, setTakeCount] = useState(0);

  const scrollRef = useRef<HTMLDivElement | null>(null);

  const { filters, onSortChange, onFilterChange } = useQueryState(schema);

  const {
    bankAndPsp: {
      info: {
        externalAccounts,
        pagination,
        generalInfo,
        casp,
        openPayNotificationVisible,
      },
    },
    configurationDictionaries: { paymentTypes, activePermissions },
    root: { currenciesList },
  } = useAppSelector((state) => state);

  const prevOpenPayNotificationVisible = usePrevious(
    openPayNotificationVisible,
  );

  const { bankId } = useParams();

  const [
    getPartner,
    { isLoading: partnerIsLoading, isFetching: partnerIsFetching },
  ] = useLazyGetPspPartnerQuery();

  const [getExternalAccountsList, { isLoading, isFetching }] =
    useLazyGetPSPExternalAccountsListQuery();
  const [getBankCasp] = useLazyGetBankCASPQuery();

  const getPartnerAction = useCallback(() => {
    getPartner(bankId as string);
  }, [bankId]);

  const prevFilters = usePrevious(filters);

  const { onFilter, setPage, errorList } = useGetList<ExternalAccount>({
    getList: getExternalAccountsList,
    onSortChange,
    onFilterChange,
    schema,
  });

  const { autocompleteList: accounts, onSearch: onSearchAccounts } =
    useGetAccountsAutocompleteList();

  const dispatch = useAppDispatch();

  const { search } = useLocation();

  const onSearchAutocomplete = (name: string, searchAutocomplete: string) => {
    if (name === 'accountId') {
      onSearchAccounts(searchAutocomplete);
    }
  };

  const { filtersComponent, setValues, handleSubmit } = useFilters({
    filtersSchema,
    filters,
    onFilterChange: onFilter,
    onSearchAutocomplete,
  });

  const formattedData = useMemo(() => {
    const data = formatExternalList(
      externalAccounts,
      paymentTypes,
      casp?.map((item) => ({ name: item.name, value: item.id })),
      currenciesList,
    );

    return data;
  }, [externalAccounts, paymentTypes, filters, casp, currenciesList]);

  useScroll({
    element: scrollRef,
    requestInProgress: isFetching,
  });

  useEffect(() => {
    setValues('accountId', accounts);
  }, [accounts]);

  useEffect(() => {
    if (bankId && activePermissions?.includes(PermissionEnum.PSP_LIST)) {
      if (!generalInfo) {
        getPartnerAction();
      }
    }
  }, [bankId, generalInfo]);

  useEffect(() => {
    if (
      bankId &&
      !externalAccounts &&
      !search &&
      !Object.keys(filters).length &&
      takeCount !== 0
    ) {
      onFilter({
        take: {
          value: [takeCount.toString()],
          type: SchemaFilterType.STRING,
        },
        pspPartnerId: {
          value: [bankId],
          type: SchemaFilterType.STRING,
        },
        active: {
          value: ['ENABLED'],
          type: SchemaFilterType.STRING,
        },
      });
    }
  }, [filters, search, externalAccounts, takeCount, bankId]);

  useEffect(() => {
    if (scrollRef && scrollRef.current) {
      const { offsetHeight } = scrollRef.current;

      if (offsetHeight) {
        const take = offsetHeight / 58;

        setTakeCount(Math.ceil(take + 3));

        dispatch(setStoreTakeCount(Math.ceil(take + 3)));
      }
    }
  }, [scrollRef]);

  useEffect(() => {
    if (
      bankId &&
      takeCount !== 0 &&
      !externalAccounts &&
      search &&
      filters &&
      prevFilters &&
      Object.keys(prevFilters).length
    ) {
      const filtersValues: any = {
        ...filters,
        take: {
          value: [takeCount.toString()],
          type: SchemaFilterType.STRING,
        },
        pspPartnerId: {
          value: [bankId],
          type: SchemaFilterType.STRING,
        },
        active: {
          value: ['ENABLED'],
          type: SchemaFilterType.STRING,
        },
      };

      onFilter(filtersValues);
    }
  }, [filters, search, externalAccounts, prevFilters, takeCount, bankId]);

  useEffect(() => {
    if (!casp) {
      getBankCasp();
    }
  }, [casp]);

  useEffect(() => {
    if (
      !openPayNotificationVisible &&
      prevOpenPayNotificationVisible &&
      bankId
    ) {
      onFilter({
        take: {
          value: [takeCount.toString()],
          type: SchemaFilterType.STRING,
        },
        pspPartnerId: {
          value: [bankId],
          type: SchemaFilterType.STRING,
        },
        active: {
          value: ['ENABLED'],
          type: SchemaFilterType.STRING,
        },
      });
    }
  }, [openPayNotificationVisible, bankId, prevOpenPayNotificationVisible]);

  useEffect(
    () => () => {
      dispatch(clearExternalAccountsState());
    },
    [],
  );

  let content;

  if (
    !activePermissions?.includes(PermissionEnum.PSP_LIST) ||
    (activePermissions?.includes(PermissionEnum.PSP_LIST) &&
      !activePermissions?.includes(PermissionEnum.PSP_INTEGRATION_ACCOUNT_LIST))
  ) {
    content = <PermissionDenied />;
  } else {
    content = (
      <div className={cn.externalBottom} ref={scrollRef}>
        <Card
          className={cn.documentsCard}
          wrapperClassName={cn.externalCard}
          isLoading={isLoading || partnerIsLoading || partnerIsFetching}
          title={
            <div className={cn.externalAccountsTitle}>
              <span className={cn.externalAccountsTitleText}>
                {`${generalInfo?.companyName} - External Accounts`}
              </span>
              {activePermissions.includes(
                PermissionEnum.PSP_INTEGRATION_ACCOUNT_ADD_ACTION,
              ) && (
                <NavLink
                  to={`${generatePath(BankRoutes.BANK_AND_PSP_EXTERNAL_CREATE, {
                    bankId: bankId || '',
                  })}${search || ''}`}
                  className={cn.addAccountLink}
                >
                  <IconExpand width={16} height={16} />
                  Add
                </NavLink>
              )}
            </div>
          }
        >
          <Table
            columns={columnsItems}
            data={formattedData || []}
            onSort={() => {}}
            sort={{}}
            filters={filters}
            error={errorList}
            columnsLabels={columnsLabels}
            onFilterChange={(values) => {
              onFilter({
                ...values,
                take: {
                  value: [takeCount.toString()],
                  type: SchemaFilterType.STRING,
                },
                pspPartnerId: {
                  value: [bankId as string],
                  type: SchemaFilterType.STRING,
                },
              });
            }}
            filtersComponent={filtersComponent}
            handleSubmit={handleSubmit}
            isLoading={isLoading || isFetching || takeCount === 0}
            storeKey="statements-of-accounts"
            pagination={pagination}
            tableBodyClassName={isLoading || isFetching ? cn.tableLoading : ''}
            tableTopClassName={cn.externalAccountsTableTop}
            take={takeCount.toString()}
            setPage={(data) => {
              setPage({
                ...data,
                filters: {
                  ...filters,
                  pspPartnerId: {
                    value: [bankId as string],
                    type: SchemaFilterType.STRING,
                  },
                },
              });
            }}
            withScroll
            clickable
          />
        </Card>
        <Outlet />
        {openPayNotificationVisible && (
          <div className={cn.reportNotification}>
            <div className={cn.reportNotificationLeft}>
              <IconLoader />
              <div className={cn.reportText}>Account will be created soon</div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return content;
};

export default PSPExternalAccounts;
